/**
 * Where everything in the network graph goes, and how the camera frames it.
 *
 * Pure geometry, kept apart from the canvas so it can be tested: which nodes
 * are connected, where organizations and their people sit in the overview and
 * around a chosen organization, which labels fit, and how far the view may
 * zoom or pan before the graph would leave the frame.
 */
import {
  linkIdentity,
  type Graph,
  type GraphLink,
  type GraphNode,
  type Point,
  type Scene,
} from "./networkGraph.model";

type SceneLike = Pick<Scene, "focusId" | "activeNodeIds"> & Partial<Pick<Scene, "activeLinkIds">>;
type Padding = { x: number; y: number };
type Frame = { x: number; y: number; zoom: number };
type Targets = Map<string, Point>;
type Endpoint = string | GraphNode | undefined;
type Positioned = GraphNode & { x: number; y: number };
export type PlacedLabel = {
  id: string;
  node: GraphNode & { x: number; y: number };
  text: string;
  selected: boolean;
  fontSize: number;
  dx: number;
  dy: number;
  align: CanvasTextAlign;
  box: { left: number; top: number; right: number; bottom: number };
};

const isPositioned = (node: GraphNode): node is Positioned =>
  Number.isFinite(node.x) && Number.isFinite(node.y);


export function connectedGraph(graph: Graph) {
  const byId = new Map(graph.nodes.map((node) => [node.id, node]));
  const adjacency = new Map<string, Set<string>>(graph.nodes.map((node) => [node.id, new Set<string>()]));
  const idOf = (endpoint: Endpoint) => (typeof endpoint === 'string' ? endpoint : endpoint?.id) ?? '';
  for (const link of graph.links) {
    const source = idOf(link.source);
    const target = idOf(link.target);
    if (!adjacency.has(source) || !adjacency.has(target)) continue;
    adjacency.get(source)!.add(target);
    adjacency.get(target)!.add(source);
  }

  const visited = new Set<string>();
  const connected = new Set<string>();
  for (const node of graph.nodes) {
    if (visited.has(node.id)) continue;
    const component: string[] = [];
    const stack = [node.id];
    visited.add(node.id);
    while (stack.length) {
      const id = stack.pop()!;
      component.push(id);
      for (const neighbor of adjacency.get(id)!) {
        if (visited.has(neighbor)) continue;
        visited.add(neighbor);
        stack.push(neighbor);
      }
    }
    if (component.filter((id) => byId.get(id)!.type === 'org').length > 1) {
      for (const id of component) connected.add(id);
    }
  }

  const nodes = graph.nodes.filter((node) => connected.has(node.id));
  const links = graph.links.filter((link) => connected.has(idOf(link.source)) && connected.has(idOf(link.target)));
  return {
    nodes,
    links,
    omittedPeople: graph.nodes.filter((node) => node.type === 'person' && !connected.has(node.id)).length,
    omittedOrganizations: graph.nodes.filter((node) => node.type === 'org' && !connected.has(node.id)).length,
  };
}

export function forceStrength(node: GraphNode, focused: boolean) {
  if (node.type === 'org') return focused ? -105 : -170;
  return focused ? -45 : -12;
}

export function lineTone(_link: GraphLink | null, focused: boolean) {
  return focused ? 'rgba(38,99,0,.34)' : 'rgba(38,99,0,.17)';
}

export function lineWeight(_link: GraphLink | null, zoom: number) {
  const pixels = zoom < 1 ? .42 : zoom < 2 ? .6 : .78;
  return pixels / Math.max(zoom, .45);
}

export function nodeImportance(node: GraphNode, scene: SceneLike, hoverId = '') {
  if (!scene.focusId || node.id === hoverId || scene.activeNodeIds.has(node.id)) return 1;
  return 0;
}

export function paintForImportance(type: 'org' | 'person' | 'link', importance: number, focused = false) {
  const amount = Math.max(0, Math.min(1, importance));
  if (type !== 'link' && amount >= 1) return '#266300';
  const quiet = type === 'org' ? .38 : type === 'person' ? .17 : .075;
  const active = type === 'link' ? (focused ? .34 : .17) : 1;
  const alpha = quiet + (active - quiet) * amount;
  return `rgba(38,99,0,${Number(alpha.toFixed(3))})`;
}

export function screenNodeRadius(type: 'org' | 'person', emphasized: boolean, zoom: number) {
  if (type === 'org') return emphasized ? (zoom < 1 ? 7.2 : 8.2) : (zoom < 1 ? 4.3 : 5.2);
  return emphasized ? (zoom < 1 ? .85 : 1.6) : (zoom < 1 ? .55 : 1.05);
}

/** Of every `n` links at this scale, one is drawn. Active links are thinned less. */
export function linkStride(isActive: boolean, scene: SceneLike, zoom: number, compact = false) {
  const activeLinks = scene.activeLinkIds ?? new Set<string>();
  return isActive
    ? Math.max(1, Math.ceil(activeLinks.size / (compact ? 450 : 1400)))
    : scene.focusId
      ? (zoom < 1 ? (compact ? 36 : 24) : zoom < 2 ? (compact ? 18 : 12) : 4)
      : (zoom < 1 ? (compact ? 24 : 12) : zoom < 2 ? (compact ? 8 : 4) : (compact ? 2 : 1));
}

/** A fixed number for a link, so the same links are the ones kept at each stride. */
export function linkHash(identity: string) {
  let hash = 2166136261;
  for (const character of identity) hash = Math.imul(hash ^ character.charCodeAt(0), 16777619);
  return hash >>> 0;
}

export function linkVisibleAtScale(link: GraphLink, scene: SceneLike, zoom: number, compact = false) {
  const identity = linkIdentity(link);
  const active = Boolean(scene.focusId && scene.activeLinkIds?.has(identity));
  const stride = linkStride(active, scene, zoom, compact);
  return stride === 1 || linkHash(identity) % stride === 0;
}

export function findOrganizationAtScreenPoint(
  nodes: GraphNode[],
  project: (node: Positioned) => Point,
  x: number,
  y: number,
  radius = 22
) {
  let closest: GraphNode | null = null;
  let closestDistance = radius;
  for (const node of nodes) {
    if (node.type !== 'org' || !isPositioned(node)) continue;
    const screen = project(node);
    const distance = Math.hypot(screen.x - x, screen.y - y);
    if (distance < closestDistance) {
      closest = node;
      closestDistance = distance;
    }
  }
  return closest;
}

export function findOrganizationLabelAtScreenPoint<T extends Pick<PlacedLabel, 'box'>>(labels: T[], x: number, y: number): T | null {
  return labels.find((label) => x >= label.box.left && x <= label.box.right
    && y >= label.box.top && y <= label.box.bottom) || null;
}

export function linkImportance(link: GraphLink, scene: SceneLike) {
  return !scene.focusId || (scene.activeLinkIds?.has(linkIdentity(link)) ?? false) ? 1 : 0;
}

// The composition keeps one shape at every size: bands around a focused
// organization stay circular rather than stretching to the frame.
export function compositionShape(_width: number, _height: number): Point {
  return { x: 1, y: 1 };
}

export function contextFrame(nodes: GraphNode[], focusId: string, width: number, height: number, padding: Padding = { x: 72, y: 72 }): Frame | null {
  const positioned = nodes.filter(isPositioned);
  if (!positioned.length) return null;
  const focus = positioned.find((node) => node.id === focusId && node.type === 'org');
  if (!focus) return null;
  const minX = Math.min(...positioned.map((node) => node.x));
  const maxX = Math.max(...positioned.map((node) => node.x));
  const minY = Math.min(...positioned.map((node) => node.y));
  const maxY = Math.max(...positioned.map((node) => node.y));
  const x = (minX + maxX) / 2 + (focus.x - (minX + maxX) / 2) * .35;
  const y = (minY + maxY) / 2 + (focus.y - (minY + maxY) / 2) * .35;
  const extentX = Math.max(Math.abs(minX - x), Math.abs(maxX - x), 1);
  const extentY = Math.max(Math.abs(minY - y), Math.abs(maxY - y), 1);
  return {
    x, y,
    zoom: Math.min((width / 2 - padding.x) / extentX, (height / 2 - padding.y) / extentY),
  };
}

export function overviewSceneFrame(nodes: GraphNode[], width: number, height: number, padding: Padding = { x: 72, y: 72 }): Frame | null {
  const positioned = nodes.filter(isPositioned);
  if (!positioned.length) return null;
  const organizations = positioned.filter((node) => node.type === 'org');
  const framed = organizations.length ? organizations : positioned;
  const bounds = (axis: 'x' | 'y') => {
    const values = positioned.map((node) => node[axis]).sort((a, b) => a - b);
    const core = framed.map((node) => node[axis]).sort((a, b) => a - b);
    return [values[0], values[values.length - 1], core[Math.floor(core.length / 2)]];
  };
  const [minX, maxX, medianX] = bounds('x');
  const [minY, maxY, medianY] = bounds('y');
  const overviewPadding = { x: Math.min(padding.x, 24), y: Math.min(padding.y, 24) };
  const massBias = framed.length >= 6 ? .5 : 0;
  const x = (minX + maxX) / 2 * (1 - massBias) + medianX * massBias;
  const y = (minY + maxY) / 2 * (1 - massBias) + medianY * massBias;
  const extentX = Math.max(1, Math.abs(minX - x), Math.abs(maxX - x));
  const extentY = Math.max(1, Math.abs(minY - y), Math.abs(maxY - y));
  return {
    x, y,
    zoom: Math.min(3.2,
      Math.max(30, width / 2 - overviewPadding.x) / extentX,
      Math.max(30, height / 2 - overviewPadding.y) / extentY),
  };
}

/** How close a selected organization is ever framed, by the width of the frame. */
export function maxFocusZoom(width: number) {
  return width < 520 ? .86 : 1.45;
}

/** The closest the camera goes by hand: a little past the closest it frames anything. */
export const MAX_ZOOM = 1.75;

/**
 * How far the camera may be zoomed by hand.
 *
 * Out as far as the whole network fitting the frame and no further, since
 * past that it only shrinks. In a little past the closest a selected
 * organization is framed: any closer and the frame holds a few dots and the
 * lines between them, with nothing to say what they belong to.
 */
export function cameraZoomBounds(overviewZoom: number) {
  const min = Math.max(.12, overviewZoom);
  return { min, max: Math.max(min, MAX_ZOOM) };
}

/** The share of organizations counted as the body of the network. */
const BODY_SHARE = .8;

/**
 * Keep the view over the network.
 *
 * The network is roughly round and `clampCameraCenter` holds the view inside
 * the square around it, whose corners are empty. This pulls the centre of the
 * view back towards the organizations until they reach well into the frame,
 * so however the graph is dragged it is never looking at an empty edge.
 */
export function keepNetworkInView(
  nodes: GraphNode[],
  center: Point,
  zoom = 1,
  width = 0,
  height = 0
): Point {
  const organizations = nodes.filter(isPositioned).filter((node) => node.type === 'org');
  if (organizations.length < 2 || !Number.isFinite(zoom) || zoom <= 0) return center;
  // The middle organization each way rather than the average, which one far
  // from the rest would drag towards itself.
  const midpoint = (key: 'x' | 'y') => {
    const values = organizations.map((node) => node[key]).sort((a, b) => a - b);
    const half = Math.floor(values.length / 2);
    return values.length % 2 ? values[half] : (values[half - 1] + values[half]) / 2;
  };
  const middle = { x: midpoint('x'), y: midpoint('y') };
  // How far out the body of the network goes: most organizations are within
  // this, and the few strung out past it do not earn the view a trip to them.
  const distances = organizations
    .map((node) => Math.hypot(node.x - middle.x, node.y - middle.y))
    .sort((a, b) => a - b);
  const reach = distances[Math.floor((distances.length - 1) * BODY_SHARE)];
  // That edge may come no nearer the middle of the frame than a third of the
  // frame's longer side, so the network always fills most of what is shown.
  const allowed = Math.max(0, reach - Math.max(width, height) / zoom / 3);
  const distance = Math.hypot(center.x - middle.x, center.y - middle.y);
  if (distance <= allowed) return center;
  if (distance === 0) return middle;
  const scale = allowed / distance;
  return {
    x: middle.x + (center.x - middle.x) * scale,
    y: middle.y + (center.y - middle.y) * scale,
  };
}

export function clampCameraCenter(
  nodes: GraphNode[],
  center: Point,
  zoom: number,
  width: number,
  height: number,
  { focusId = '', anchor = center }: { focusId?: string; anchor?: Point } = {}
): Point {
  const positioned = nodes.filter(isPositioned);
  if (!positioned.length || !Number.isFinite(zoom) || zoom <= 0) return center;
  const clamp = (value: number, low: number, high: number) => Math.min(high, Math.max(low, value));
  const axis = (coordinate: number, viewportSize: number, key: 'x' | 'y') => {
    const values = positioned.map((node) => node[key]);
    const min = Math.min(...values);
    const max = Math.max(...values);
    const halfView = viewportSize / (2 * zoom);
    const margin = 16 / zoom;
    if (max - min + margin * 2 <= halfView * 2) {
      if (!focusId) return anchor[key];
      const allowance = (max - min) * .2;
      return clamp(coordinate, min - allowance, max + allowance);
    }
    return clamp(coordinate, min + halfView - margin, max - halfView + margin);
  };
  return { x: axis(center.x, width, 'x'), y: axis(center.y, height, 'y') };
}

export function focusFrame(nodes: GraphNode[], focusId: string, width: number, height: number, padding: Padding = { x: 72, y: 72 }): Frame | null {
  const focus = nodes.filter(isPositioned).find((node) => node.id === focusId);
  if (!focus) return null;
  const organizations = nodes.filter(isPositioned).filter((node) => node.type === 'org');
  const extentX = Math.max(24, ...organizations.map((node) => Math.abs(node.x - focus.x)));
  const extentY = Math.max(24, ...organizations.map((node) => Math.abs(node.y - focus.y)));
  const usableWidth = Math.max(60, width - padding.x * 2);
  const usableHeight = Math.max(60, height - padding.y * 2);
  return {
    x: focus.x,
    y: focus.y,
    zoom: Math.min(3.2, usableWidth / (extentX * 2), usableHeight / (extentY * 2)),
  };
}

export function focusSceneFrame(
  allNodes: GraphNode[],
  scene: SceneLike,
  width: number,
  height: number,
  padding: Padding = { x: 72, y: 72 },
  links: GraphLink[] = []
): Frame | null {
  if (!scene.focusId) return null;
  const nodes = allNodes.filter(isPositioned);
  const focus = nodes.find((node) => node.id === scene.focusId);
  if (!focus) return null;
  const directPeople = new Set<string | undefined>();
  for (const link of links) {
    const source = typeof link.source === 'string' ? link.source : link.source?.id;
    const target = typeof link.target === 'string' ? link.target : link.target?.id;
    if (source === scene.focusId) directPeople.add(target);
    if (target === scene.focusId) directPeople.add(source);
  }
  const relatedOrganizations = nodes.filter((node) => node.type === 'org' && node.id !== scene.focusId
    && scene.activeNodeIds.has(node.id));
  const relatedRadius = relatedOrganizations.length
    ? Math.max(...relatedOrganizations.map((node) => Math.hypot(node.x - focus.x, node.y - focus.y)))
    : Infinity;
  const neighborhood = nodes.filter((node) => scene.activeNodeIds.has(node.id)
    && (node.type === 'org' || (directPeople.has(node.id)
      && Math.hypot(node.x - focus.x, node.y - focus.y) <= relatedRadius + 32)
      || (!links.length && node.type === 'person')));
  const extentX = Math.max(60, ...neighborhood.map((node) => Math.abs(node.x - focus.x)));
  const extentY = Math.max(60, ...neighborhood.map((node) => Math.abs(node.y - focus.y)));
  // Keep the first two visual tiers legible. Distant active participant halos
  // and the dimmed outer network can extend past the camera frame.
  const preview = Math.max(40, Math.min(70, Math.max(extentX, extentY) * .08));
  const frameZoom = Math.min(3.2,
    Math.max(60, width - padding.x * 2) / ((extentX + preview) * 2),
    Math.max(60, height - padding.y * 2) / ((extentY + preview) * 2));
  const focusMagnification = width < 520 ? 1.12 : 1.2;
  const closest = maxFocusZoom(width);
  const organizationEdgeMargin = 24;
  const ringSafetyZoom = Number.isFinite(relatedRadius)
    ? Math.min(width / 2 - organizationEdgeMargin, height / 2 - organizationEdgeMargin) / Math.max(1, relatedRadius)
    : Infinity;
  return { x: focus.x, y: focus.y,
    zoom: Math.min(frameZoom * focusMagnification, closest, ringSafetyZoom) };
}

// Keep each organization's participant circle intact while reorganizing the
// whole network around the chosen institution.
function haloRadius(count: number) {
  return count <= 32 ? Math.max(30, Math.min(72, 28 + count * 1.5))
    : Math.min(96, 45 + Math.sqrt(count) * 2.5);
}

function circularParticipantTargets(
  nodes: GraphNode[],
  links: GraphLink[],
  baseline: Targets,
  targets: Targets,
  compactOrganizations: Set<string> = new Set()
): Targets {
  const organizations = new Set(nodes.filter((node) => node.type === 'org').map((node) => node.id));
  const personOrganizations = new Map<string, Set<string>>();
  const idOf = (endpoint: Endpoint) => (typeof endpoint === 'string' ? endpoint : endpoint?.id) ?? '';
  for (const link of links) {
    const source = idOf(link.source);
    const target = idOf(link.target);
    const organization = organizations.has(source) ? source : organizations.has(target) ? target : null;
    const person = organization === source ? target : source;
    if (!organization || organizations.has(person)) continue;
    if (!personOrganizations.has(person)) personOrganizations.set(person, new Set());
    personOrganizations.get(person)!.add(organization);
  }
  const singleOrganizationGroups = new Map<string, string[]>();
  for (const [person, linkedOrganizations] of personOrganizations) {
    if (linkedOrganizations.size !== 1) continue;
    const organization = [...linkedOrganizations][0];
    if (!singleOrganizationGroups.has(organization)) singleOrganizationGroups.set(organization, []);
    singleOrganizationGroups.get(organization)!.push(person);
  }
  for (const [organization, members] of singleOrganizationGroups) {
    const center = targets.get(organization);
    if (!center) continue;
    members.sort();
    const radiusScale = compactOrganizations.has(organization) ? .56 : 1;
    const outerRadius = haloRadius(members.length);
    const phase = [...organization].reduce((value, character) => value * 31 + character.charCodeAt(0), 0) % 360 * Math.PI / 180;
    members.forEach((person, index) => {
      const angle = phase + (members.length <= 32 ? index * 2 * Math.PI / members.length : index * 2.3999632297);
      const radius = (members.length <= 32 ? outerRadius
        : 23 + (outerRadius - 23) * Math.sqrt((index + .5) / members.length)) * radiusScale;
      targets.set(person, { x: center.x + Math.cos(angle) * radius, y: center.y + Math.sin(angle) * radius });
    });
  }
  for (const [person, linkedOrganizations] of personOrganizations) {
    if (linkedOrganizations.size < 2) continue;
    const organizationsForPerson = [...linkedOrganizations].filter((id) => targets.has(id) && baseline.has(id));
    if (!organizationsForPerson.length) continue;
    const center = organizationsForPerson.reduce((result, id) => ({
      x: result.x + targets.get(id)!.x / organizationsForPerson.length,
      y: result.y + targets.get(id)!.y / organizationsForPerson.length,
    }), { x: 0, y: 0 });
    const originalCenter = organizationsForPerson.reduce((result, id) => ({
      x: result.x + baseline.get(id)!.x / organizationsForPerson.length,
      y: result.y + baseline.get(id)!.y / organizationsForPerson.length,
    }), { x: 0, y: 0 });
    const original = baseline.get(person);
    if (!original) continue;
    const dx = original.x - originalCenter.x;
    const dy = original.y - originalCenter.y;
    const offset = Math.min(18, Math.hypot(dx, dy)) / Math.max(1, Math.hypot(dx, dy));
    targets.set(person, { x: center.x + dx * offset, y: center.y + dy * offset });
  }
  return targets;
}

function spreadOverviewTargets(nodes: GraphNode[], targets: Targets): Targets {
  const organizations = nodes.filter((node) => node.type === 'org' && targets.has(node.id));
  if (organizations.length < 4) return targets;
  const median = (values: number[]) => {
    const sorted = values.sort((a, b) => a - b);
    return sorted[Math.floor(sorted.length / 2)];
  };
  const center = {
    x: median(organizations.map((node) => targets.get(node.id)!.x)),
    y: median(organizations.map((node) => targets.get(node.id)!.y)),
  };
  const distances = organizations.map((node) => Math.hypot(
    targets.get(node.id)!.x - center.x, targets.get(node.id)!.y - center.y,
  )).sort((a, b) => a - b);
  const radius = distances[Math.floor((distances.length - 1) * .85)];
  if (radius < 1) return targets;
  for (const [id, point] of targets) {
    const dx = point.x - center.x;
    const dy = point.y - center.y;
    const distance = Math.hypot(dx, dy);
    if (!distance) continue;
    const expanded = distance < radius
      ? radius * (distance / radius) ** .56
      : radius * (1 + .1 * (1 - Math.exp(-(distance - radius) / radius)));
    targets.set(id, {
      x: center.x + dx * expanded / distance,
      y: center.y + dy * expanded / distance,
    });
  }
  return targets;
}

export function focusLayoutTargets(
  nodes: GraphNode[],
  scene: SceneLike,
  baseline: Targets,
  links: GraphLink[] = [],
  shape: Point = { x: 1, y: 1 }
): Targets {
  const at = (node: GraphNode): Point => baseline.get(node.id) ?? { x: node.x ?? 0, y: node.y ?? 0 };
  const targets: Targets = new Map(nodes.map((node) => {
    const point = at(node);
    return [node.id, { x: point.x * shape.x, y: point.y * shape.y }];
  }));
  if (!scene.focusId) return circularParticipantTargets(nodes, links, baseline,
    spreadOverviewTargets(nodes, targets));
  const focus = targets.get(scene.focusId);
  if (!focus || !Number.isFinite(focus.x) || !Number.isFinite(focus.y)) return circularParticipantTargets(nodes, links, baseline, targets);
  const baselineFocus = baseline.get(scene.focusId) || focus;
  const organizations = nodes.filter((node) => node.type === 'org' && node.id !== scene.focusId);
  const related = organizations.filter((node) => scene.activeNodeIds.has(node.id));
  const remaining = organizations.filter((node) => !scene.activeNodeIds.has(node.id));
  const relatedRadius = Math.max(135, Math.min(255, 125 + related.length * 3.5));
  const contextRadius = relatedRadius + 165;
  const placeBand = (members: GraphNode[], radius: number, step = 0) => {
    const ordered = members.map((node) => {
      const point = at(node);
      return { node, angle: Math.atan2(point.y - baselineFocus.y, point.x - baselineFocus.x) };
    }).sort((a, b) => a.angle - b.angle || a.node.id.localeCompare(b.node.id));
    const phase = ordered[0]?.angle || 0;
    ordered.forEach(({ node }, index) => {
      const angle = phase + index * 2 * Math.PI / ordered.length;
      const distance = radius + (step ? (index % 3) * step : 0);
      targets.set(node.id, { x: focus.x + Math.cos(angle) * distance * shape.x, y: focus.y + Math.sin(angle) * distance * shape.y });
    });
  };
  placeBand(related, relatedRadius);
  placeBand(remaining, contextRadius, 28);

  const affiliatedPeople = new Set<string>();
  const orgIds = new Set(nodes.filter((node) => node.type === 'org').map((node) => node.id));
  for (const link of links) {
    const source = typeof link.source === 'string' ? link.source : link.source.id;
    const target = typeof link.target === 'string' ? link.target : link.target.id;
    if (orgIds.has(source) && !orgIds.has(target)) affiliatedPeople.add(target);
    if (orgIds.has(target) && !orgIds.has(source)) affiliatedPeople.add(source);
  }
  for (const node of nodes) {
    if (node.type !== 'person' || affiliatedPeople.has(node.id)) continue;
    const point = at(node);
    const angle = Math.atan2(point.y - baselineFocus.y, point.x - baselineFocus.x);
    const radius = scene.activeNodeIds.has(node.id)
      ? Math.min(relatedRadius - 45, Math.max(75, Math.hypot(point.x - focus.x, point.y - focus.y) * .35))
      : contextRadius + 55 + Math.min(120, Math.hypot(point.x - focus.x, point.y - focus.y) * .08);
    targets.set(node.id, { x: focus.x + Math.cos(angle) * radius * shape.x, y: focus.y + Math.sin(angle) * radius * shape.y });
  }
  return circularParticipantTargets(nodes, links, baseline, targets, scene.activeNodeIds);
}

function labelBudget({ focusId, compact, zoom }: { focusId: string; compact: boolean; zoom: number }) {
  if (focusId) {
    if (compact) return zoom < 1.4 ? 3 : zoom < 2.2 ? 5 : 8;
    return zoom < 1.1 ? 10 : zoom < 2.2 ? 16 : 24;
  }
  if (compact) return zoom < 1.4 ? 0 : zoom < 2.2 ? 2 : 4;
  return zoom < 1.1 ? 4 : zoom < 2.2 ? 6 : 9;
}

function shorten(name: string, length: number) {
  return name.length > length ? `${name.slice(0, length - 1)}…` : name;
}

type Box = PlacedLabel['box'];
function overlaps(a: Box, b: Box, gap = 6) {
  return a.left < b.right + gap && a.right + gap > b.left
    && a.top < b.bottom + gap && a.bottom + gap > b.top;
}

export function planOrganizationLabels(
  nodes: GraphNode[],
  options: {
    focusId?: string;
    width: number;
    height: number;
    compact?: boolean;
    zoom: number;
    project: (node: Positioned) => Point;
    measure: (text: string, selected: boolean) => number;
  }
): PlacedLabel[] {
  const { focusId = '', width, height, compact = false, zoom, project, measure } = options;
  const budget = labelBudget({ focusId, compact, zoom });
  const candidates = nodes.filter(isPositioned).filter((node) => node.type === 'org')
    .sort((a, b) => Number(b.id === focusId) - Number(a.id === focusId)
      || (b.degree || 0) - (a.degree || 0)
      || (a.name ?? '').localeCompare(b.name ?? ''));
  const placed: PlacedLabel[] = [];
  const boundary = 10;
  for (const node of candidates) {
    if (placed.length >= budget) break;
    const selected = node.id === focusId;
    const text = shorten(node.name ?? '', compact ? 25 : 43);
    const fontSize = selected ? 15 : 13;
    const textWidth = measure(text, selected);
    const { x, y } = project(node);
    const offset = selected ? 22 : 18;
    const attempts: { dx: number; dy: number; align: CanvasTextAlign }[] = [
      { dx: offset, dy: 0, align: 'left' },
      { dx: -offset, dy: 0, align: 'right' },
      { dx: 0, dy: -offset, align: 'center' },
      { dx: 0, dy: offset, align: 'center' },
    ];
    for (const attempt of attempts) {
      const centerX = x + attempt.dx;
      const centerY = y + attempt.dy;
      const left = attempt.align === 'left' ? centerX : attempt.align === 'right' ? centerX - textWidth : centerX - textWidth / 2;
      const box = { left, top: centerY - fontSize / 2 - 2, right: left + textWidth, bottom: centerY + fontSize / 2 + 2 };
      if (box.left < boundary || box.right > width - boundary || box.top < boundary || box.bottom > height - boundary) continue;
      if (placed.some((other) => overlaps(box, other.box))) continue;
      placed.push({ id: node.id, node, text, selected, fontSize, ...attempt, box });
      break;
    }
  }
  return placed;
}
